import ActivixDate from '@/value-objects/ActivixDate.js';
import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

interface AuthProvider {
    jwtSub(): string | false;
}

interface CacheObject {
    key?: string;
    value: unknown;
    expire_at: number | null;
    version: string | undefined;
    sub: string | false | undefined;
}

// Injected via setAuthProvider() to break circular dependency with auth.ts
let authProvider: AuthProvider | null = null;

// Singleton instance set during install()
let cachingInstance: Caching | null = null;

export function getCaching(): Caching | null {
    return cachingInstance;
}

export function setAuthProvider(auth: AuthProvider): void {
    authProvider = auth;
}

class Caching {
    static CONTEXT_ACCOUNT_CACHE_KEY = 'contextAccountCache';

    static AUTH_USER_CACHE_KEY = 'authUserCache';

    static DASHBOARD_VIEWS_CACHE_KEY = 'dashboardViewsCache';

    static CALENDAR_VIEWS_CACHE_KEY = 'calendarViewsCache';

    static SHARED_CALENDAR_VIEWS_CACHE_KEY = 'sharedCalendarViewsCache';

    static USER_SESSION_CACHE_KEY = 'userSessionCache';

    static SHARED_DASHBOARD_VIEWS_CACHE_KEY = 'sharedDashboardViewsCache';

    db: IDBDatabase | null = null;

    constructor() {
        const request = indexedDB.open('CRM', 1);

        request.onsuccess = (event) => {
            this.db = (event.target as IDBOpenDBRequest).result;
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains('cache')) {
                db.createObjectStore('cache', { keyPath: 'key' });
            }
        };

        request.onerror = () => {
            request.onsuccess = null;
            request.onupgradeneeded = null;
        };
    }

    getStore(): Promise<IDBObjectStore | null> {
        return new Promise((resolve) => {
            try {
                if (this.db === null) {
                    setTimeout(() => {
                        resolve(this.getStore());
                    }, 100);
                } else {
                    const transaction = this.db.transaction(['cache'], 'readwrite');
                    resolve(transaction.objectStore('cache'));
                }
            } catch {
                resolve(null);
            }
        });
    }

    static install(app: App): void {
        const cache = new Caching();
        cachingInstance = cache;
        app.config.globalProperties.$caching = cache;

        const scheduleCacheClear = () => {
            clearOldCache().then(() => {
                setTimeout(scheduleCacheClear, 300000);
            });
        };

        const clearOldCache = (): Promise<void> => {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                try {
                    const store = await cache.getStore();

                    if (!store) {
                        resolve();
                        return;
                    }

                    const allCacheRequest = store.openCursor();

                    allCacheRequest.onsuccess = (event) => {
                        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>)
                            .result;

                        if (cursor) {
                            if (!Caching.isCurrentlyValid(cursor.value)) {
                                cursor.delete();
                            }

                            cursor.continue();
                        }

                        resolve();
                    };

                    allCacheRequest.onerror = (event) => {
                        Sentry.captureMessage(
                            `[IndexDB Cache error] ${(event.target as IDBRequest).error}`,
                            'error'
                        );
                        reject((event.target as IDBRequest).error);
                    };
                } catch (e) {
                    Sentry.captureException(e);
                    reject(e);
                }
            });
        };

        scheduleCacheClear();
    }

    static isCurrentlyValid(cacheObject: CacheObject): boolean {
        try {
            return (
                typeof cacheObject !== 'undefined' &&
                (cacheObject.expire_at! > new ActivixDate('now').timestamp! ||
                    cacheObject.expire_at === null) &&
                cacheObject.version === process.env.VUE_APP_VERSION &&
                cacheObject.sub === authProvider?.jwtSub()
            );
        } catch {
            return false;
        }
    }

    async setCache(key: string, value: unknown, duration = 900): Promise<void> {
        try {
            const expireAt = duration
                ? new ActivixDate('now').addSeconds(duration).timestamp
                : null;
            const cacheObject: CacheObject = {
                value,
                expire_at: expireAt,
                version: process.env.VUE_APP_VERSION,
                sub: authProvider?.jwtSub(),
            };

            (await this.getStore())?.put(cacheObject, key);
        } catch {
            // Do nothing
        }
    }

    async deleteFromCache(key: string): Promise<void> {
        try {
            (await this.getStore())?.delete(key);
        } catch {
            // Do nothing
        }
    }

    getCache(key: string): Promise<unknown> {
        return new Promise((resolve) => {
            this.getStore().then((store) => {
                if (!store) {
                    resolve(null);
                    return;
                }

                const cacheObjectRequest = store.get(key);

                try {
                    cacheObjectRequest.onsuccess = () => {
                        if (Caching.isCurrentlyValid(cacheObjectRequest.result)) {
                            resolve(cacheObjectRequest.result.value);
                        } else {
                            resolve(null);
                        }
                    };

                    cacheObjectRequest.onerror = () => {
                        resolve(null);
                    };
                } catch {
                    resolve(null);
                }
            });
        });
    }

    async clear(): Promise<void> {
        (await this.getStore())?.clear();
    }
}

export default Caching;
